import React, { useEffect, useMemo, useState } from "react";
import { EcosystemAPI, mapServerError } from "../../lib/ecosystemApi";

/**
 * Global audit log: aggregates audit events across every entity the
 * current operator can read.  The frontend does not have a dedicated
 * global-audit endpoint — it fans out via existing per-entity audit
 * reads.  This is a documented contract limitation: cost-of-B.3
 * scope containment (no new backend endpoints).
 */
export default function AuditLog() {
  const [rows, setRows] = useState([]);
  const [err, setErr] = useState(null);
  const [filterEnhanced, setFilterEnhanced] = useState(false);
  const [filterActor, setFilterActor] = useState("");
  const [filterEvent, setFilterEvent] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const entities = await EcosystemAPI.list();
        const all = [];
        for (const e of entities) {
          const a = await EcosystemAPI.audit(e.id).catch(() => []);
          a.forEach((row) => all.push({ ...row, _canonical: e.canonical_name }));
        }
        all.sort((a, b) => (b.occurred_at || "").localeCompare(a.occurred_at || ""));
        setRows(all);
      } catch (e) { setErr(mapServerError(e)); }
    })();
  }, []);

  const filtered = useMemo(() => rows.filter((r) => {
    if (filterEnhanced && !r.enhanced_review) return false;
    if (filterActor && !(r.actor_email_at_time || "").toLowerCase().includes(filterActor.toLowerCase())) return false;
    if (filterEvent && r.event_type !== filterEvent) return false;
    return true;
  }), [rows, filterEnhanced, filterActor, filterEvent]);

  const events = [...new Set(rows.map((r) => r.event_type))].sort();

  return (
    <div className="space-y-3" data-testid="ecosystem-audit-log">
      <h1 className="text-2xl font-semibold">Audit log</h1>
      <p className="text-xs text-slate-500">Read-only. Records are permanent.</p>
      {err && <div className="text-sm text-red-700">{err}</div>}

      <div className="flex gap-2 items-center bg-white p-3 rounded border flex-wrap" data-testid="audit-filters">
        <label className="text-sm flex items-center gap-1">
          <input data-testid="audit-filter-enhanced" type="checkbox" checked={filterEnhanced} onChange={(e) => setFilterEnhanced(e.target.checked)} /> Enhanced only
        </label>
        <input data-testid="audit-filter-actor" className="text-sm border rounded p-1" placeholder="Actor email…" value={filterActor} onChange={(e) => setFilterActor(e.target.value)} />
        <select data-testid="audit-filter-event" className="text-sm border rounded p-1" value={filterEvent} onChange={(e) => setFilterEvent(e.target.value)}>
          <option value="">Any event</option>
          {events.map((e) => <option key={e}>{e}</option>)}
        </select>
      </div>

      <ul className="space-y-2">
        {filtered.map((r) => (
          <li key={r.id} data-testid={`audit-row-${r.id}`} className="bg-white p-3 rounded border text-sm">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs text-slate-500">{r.occurred_at}</span>
              <span className="font-medium">{r.event_type}</span>
              {r.enhanced_review && <span className="text-xs px-1.5 py-0.5 rounded-full border border-amber-400 bg-amber-50 text-amber-900">Enhanced</span>}
              <span className="text-xs text-slate-500 ml-auto">{r._canonical}</span>
            </div>
            <div className="text-xs text-slate-600">
              {r.actor_email_at_time} ({r.actor_role_at_time}) — {r.change_summary}
            </div>
            {r.enhanced_review && r.enhanced_triggers && (
              <div className="text-xs text-amber-800 mt-1">
                Triggers: {(r.enhanced_triggers.domains || []).join(", ")}{(r.enhanced_triggers.categories || []).length ? " · " + r.enhanced_triggers.categories.join(", ") : ""}
              </div>
            )}
          </li>
        ))}
        {filtered.length === 0 && !err && <div className="text-sm text-slate-500">No audit events match.</div>}
      </ul>
    </div>
  );
}
